import { useEffect, useRef, useState } from 'react';
import { navigate } from '../app/router.ts';
import { getItem } from '../data/catalog.ts';
import { usePlayer } from '../player/PlayerProvider.tsx';
import { formatDuration } from '../shared/spotifyUri.ts';
import type { CatalogItem } from '../data/db.ts';

const SKIP_MS = 15_000;

/**
 * Abspielbildschirm. Bewusst reduziert: ein grosser Knopf in der Mitte,
 * zwei Sprungtasten, ein Weg zurueck. Kein Titelwechsel, keine Playlist.
 */
export function PlayerScreen({ itemId }: { itemId: string }) {
  const {
    playback,
    positionMs,
    chapterIndex,
    isPlaying,
    startItem,
    togglePlayPause,
    skip,
    skipChapter,
  } = usePlayer();
  const [item, setItem] = useState<CatalogItem | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void getItem(itemId).then((result) => {
      if (!cancelled) setItem(result ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  // Beim Öffnen automatisch starten – aber nicht neu starten, wenn die Folge
  // bereits läuft (sonst springt sie beim Zurückkehren an den Anfang).
  useEffect(() => {
    if (!item || startedRef.current) return;
    startedRef.current = true;
    if (playback?.context?.uri === item.uri) return;
    void startItem(item).catch(() => {
      // Fehlende Geräte behandelt der PlayerProvider über den Erwachsenen-Bildschirm.
    });
  }, [item, playback?.context?.uri, startItem]);

  const durationMs = playback?.item?.duration_ms ?? 0;
  const fraction = durationMs > 0 ? Math.min(1, positionMs / durationMs) : 0;
  const trackCount = item?.trackCount ?? 0;

  return (
    <div className="kids player">
      <header className="kids__bar">
        <button
          type="button"
          className="iconbutton iconbutton--home"
          onClick={() => navigate('/')}
          aria-label="Zurück zur Startseite"
        >
          <span aria-hidden="true">⌂</span>
        </button>
      </header>

      <div className="player__art">
        {item?.imageUrl ? (
          <img src={item.imageUrl} alt="" draggable={false} />
        ) : (
          <span aria-hidden="true">♪</span>
        )}
      </div>

      <div className="player__controls">
        <button
          type="button"
          className="iconbutton iconbutton--chapter"
          onClick={() => void skipChapter(-1)}
          aria-label="Vorheriges Kapitel"
        >
          <span aria-hidden="true">⏮</span>
        </button>

        <button
          type="button"
          className="iconbutton iconbutton--skip"
          onClick={() => void skip(-SKIP_MS)}
          aria-label="15 Sekunden zurück"
        >
          <span aria-hidden="true">↺</span>
        </button>

        <button
          type="button"
          className="iconbutton iconbutton--play"
          onClick={() => void togglePlayPause()}
          aria-label={isPlaying ? 'Pause' : 'Abspielen'}
        >
          <span aria-hidden="true">{isPlaying ? '❚❚' : '▶'}</span>
        </button>

        <button
          type="button"
          className="iconbutton iconbutton--skip"
          onClick={() => void skip(SKIP_MS)}
          aria-label="15 Sekunden vor"
        >
          <span aria-hidden="true">↻</span>
        </button>

        <button
          type="button"
          className="iconbutton iconbutton--chapter"
          onClick={() => void skipChapter(1)}
          aria-label="Nächstes Kapitel"
        >
          <span aria-hidden="true">⏭</span>
        </button>
      </div>

      {/* Oben: Position im laufenden Kapitel. */}
      <div className="player__progress" aria-hidden="true">
        <div className="player__progress-value" style={{ width: `${fraction * 100}%` }} />
      </div>

      {/* Darunter: ein Block je Kapitel – zeigt, wie weit die Folge insgesamt ist. */}
      {trackCount > 0 && (
        <div
          className="player__chapters"
          role="img"
          aria-label={`Kapitel ${chapterIndex + 1} von ${trackCount}`}
        >
          {Array.from({ length: trackCount }, (_, index) => (
            <span
              key={index}
              className={
                index < chapterIndex
                  ? 'player__chapter is-done'
                  : index === chapterIndex
                    ? 'player__chapter is-current'
                    : 'player__chapter'
              }
            />
          ))}
        </div>
      )}

      {/* Kleine Textzeile fuer Erwachsene, fuer das Kind irrelevant. */}
      <p className="player__meta">
        {playback?.item?.name ?? item?.title ?? ''} · Kapitel {chapterIndex + 1} von {trackCount} ·{' '}
        {formatDuration(positionMs)} / {formatDuration(durationMs)}
      </p>
    </div>
  );
}
